import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-[#005A2C] shadow-2xl backdrop-blur-md' 
        : 'bg-[#005A2C] shadow-xl backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="brand-wrap">{/* uses CSS to align logo + title */}
            <Link to="/" className="relative group">
              <img 
                src="https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/995999b745e34f0ed4248820e32af9bb.jpeg" 
                alt="Elite College Logo" 
                className="h-10 w-10 md:h-12 md:w-12 object-cover rounded-full ring-4 ring-[#C6A44C]/30 group-hover:ring-[#C6A44C]/60 transition-all duration-300 cursor-pointer"
                style={{ objectPosition: 'center' }}
              />
            </Link>
            <Link to="/" className="ml-3 flex flex-col justify-center brand-title text-white hover:text-[#C6A44C] transition-colors duration-300"> 
              <span className="brand-line-top leading-none">Elite</span>
              <span className="brand-line-bottom leading-none">College</span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-1">
              <Link 
                to="/" 
                className={`relative px-4 py-3 text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 rounded-lg group ${
                  isActive('/') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                Home
                {isActive('/') && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-[#C6A44C] rounded-full"></div>
                )}
              </Link>
              <Link 
                to="/about" 
                className={`relative px-4 py-3 text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 rounded-lg group ${
                  isActive('/about') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                About
                {isActive('/about') && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-[#C6A44C] rounded-full"></div>
                )}
              </Link>
              <Link 
                to="/admissions" 
                className={`relative px-4 py-3 text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 rounded-lg group ${
                  isActive('/admissions') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                Admissions
                {isActive('/admissions') && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-[#C6A44C] rounded-full"></div>
                )}
              </Link>
              <Link 
                to="/gallery" 
                className={`relative px-4 py-3 text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 rounded-lg group ${
                  isActive('/gallery') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                Gallery
                {isActive('/gallery') && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-[#C6A44C] rounded-full"></div>
                )}
              </Link>
              <Link 
                to="/contact" 
                className={`relative px-4 py-3 text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 rounded-lg group ${
                  isActive('/contact') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                Contact
                {isActive('/contact') && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-[#C6A44C] rounded-full"></div>
                )}
              </Link>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={toggleMenu}
              className="text-white hover:text-[#C6A44C] cursor-pointer p-3 focus:outline-none focus:ring-2 focus:ring-[#C6A44C] rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-300"
              aria-label="Toggle menu"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#005A2C] border-t border-[#007A3D] shadow-2xl">
            <div className="px-4 py-6 space-y-2">
              <Link 
                to="/" 
                onClick={closeMenu}
                className={`block px-4 py-3 text-base font-semibold cursor-pointer transition-all duration-300 rounded-xl ${
                  isActive('/') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                Home
              </Link>
              <Link 
                to="/about" 
                onClick={closeMenu}
                className={`block px-4 py-3 text-base font-semibold cursor-pointer transition-all duration-300 rounded-xl ${
                  isActive('/about') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                About
              </Link>
              <Link 
                to="/admissions" 
                onClick={closeMenu}
                className={`block px-4 py-3 text-base font-semibold cursor-pointer transition-all duration-300 rounded-xl ${
                  isActive('/admissions') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                Admissions
              </Link>
              <Link 
                to="/gallery" 
                onClick={closeMenu}
                className={`block px-4 py-3 text-base font-semibold cursor-pointer transition-all duration-300 rounded-xl ${
                  isActive('/gallery') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                Gallery
              </Link>
              <Link 
                to="/contact" 
                onClick={closeMenu}
                className={`block px-4 py-3 text-base font-semibold cursor-pointer transition-all duration-300 rounded-xl ${
                  isActive('/contact') 
                    ? 'text-[#C6A44C] bg-white/10' 
                    : 'text-white hover:text-[#C6A44C] hover:bg-white/5'
                }`}
              >
                Contact
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
